import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, delay } from 'rxjs/operators';
import { WhitelistAddressService } from '../services/whitelist-address.service';
import * as WhitelistAddressActions from './whitelist-address.actions';
import * as AssetManagementActions from '../../shared/store/asset-management.actions';

@Injectable()
export class WhitelistAddressEffects {
  private readonly translate = inject(TranslateService);

  createAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WhitelistAddressActions.createWhitelistAddress),
      switchMap(({ request }) =>
        this.whitelistAddressService.createWhitelistAddress(request).pipe(
          map(() => WhitelistAddressActions.createWhitelistAddressSuccess()),
          catchError(error =>
            of(
              WhitelistAddressActions.createWhitelistAddressFailure({
                error:
                  error.message ||
                  this.translate.instant(
                    'assets.whitelist.errors.createFailed'
                  ),
              })
            )
          )
        )
      )
    )
  );

  createAddressSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WhitelistAddressActions.createWhitelistAddressSuccess),
      delay(1800),
      map(() => AssetManagementActions.loadWhitelistAddresses())
    )
  );

  enableAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WhitelistAddressActions.enableWhitelistAddress),
      switchMap(({ addressWhitelistId }) =>
        this.whitelistAddressService
          .enableWhitelistAddress(addressWhitelistId)
          .pipe(
            map(() => {
              // Reload the list after successful enable
              return WhitelistAddressActions.enableWhitelistAddressSuccess();
            }),
            catchError(error =>
              of(
                WhitelistAddressActions.enableWhitelistAddressFailure({
                  error:
                    error.message ||
                    this.translate.instant(
                      'assets.whitelist.errors.enableFailed'
                    ),
                })
              )
            )
          )
      )
    )
  );

  enableAddressSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WhitelistAddressActions.enableWhitelistAddressSuccess),
      delay(1800),
      map(() => AssetManagementActions.loadWhitelistAddresses())
    )
  );

  disableAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WhitelistAddressActions.disableWhitelistAddress),
      switchMap(({ addressWhitelistId }) =>
        this.whitelistAddressService
          .disableWhitelistAddress(addressWhitelistId)
          .pipe(
            map(() => {
              // Reload the list after successful disable
              return WhitelistAddressActions.disableWhitelistAddressSuccess();
            }),
            catchError(error =>
              of(
                WhitelistAddressActions.disableWhitelistAddressFailure({
                  error:
                    error.message ||
                    this.translate.instant(
                      'assets.whitelist.errors.disableFailed'
                    ),
                })
              )
            )
          )
      )
    )
  );

  disableAddressSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WhitelistAddressActions.disableWhitelistAddressSuccess),
      delay(1800),
      map(() => AssetManagementActions.loadWhitelistAddresses())
    )
  );

  deleteAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WhitelistAddressActions.deleteWhitelistAddress),
      switchMap(({ addressWhitelistId }) =>
        this.whitelistAddressService
          .deleteWhitelistAddress(addressWhitelistId)
          .pipe(
            map(() => {
              // Reload the list after successful delete
              return WhitelistAddressActions.deleteWhitelistAddressSuccess();
            }),
            catchError(error =>
              of(
                WhitelistAddressActions.deleteWhitelistAddressFailure({
                  error:
                    error.message ||
                    this.translate.instant(
                      'assets.whitelist.errors.deleteFailed'
                    ),
                })
              )
            )
          )
      )
    )
  );

  deleteAddressSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WhitelistAddressActions.deleteWhitelistAddressSuccess),
      delay(1800),
      map(() => AssetManagementActions.loadWhitelistAddresses())
    )
  );

  constructor(
    private actions$: Actions,
    private whitelistAddressService: WhitelistAddressService
  ) {}
}
